import React, { useState, useEffect } from 'react';
import './Dashboard.css'; // or './App.css'


const CompanyDashboard = ({ userData, onLogout }) => {
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [sortBy, setSortBy] = useState('');
  const [filter, setFilter] = useState({
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
  });

  // Dropdown states
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  useEffect(() => {
    if (userData) {
      setMembers(userData.members || []);
      setTransactions(userData.transactions || []);
      setFilteredTransactions(userData.transactions || []);
    }
  }, [userData]);

  // Remove auto-apply useEffect - filters will only apply when user clicks Apply

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleMemberFilter = (memberId) => {
    setSelectedMember(memberId === 'all' ? null : Number(memberId));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    if (selectedMember) {
      filtered = filtered.filter((t) => t.Mem_Id === selectedMember);
    }

    if (filter.startDate) {
      const startDate = new Date(filter.startDate);
      filtered = filtered.filter((t) => new Date(t.Payment_Date) >= startDate);
    }

    if (filter.endDate) {
      const endDate = new Date(filter.endDate);
      endDate.setHours(23, 59, 59);
      filtered = filtered.filter((t) => new Date(t.Payment_Date) <= endDate);
    }

    if (filter.minAmount) {
      filtered = filtered.filter((t) => Number(t.Amount) >= Number(filter.minAmount));
    }

    if (filter.maxAmount) {
      filtered = filtered.filter((t) => Number(t.Amount) <= Number(filter.maxAmount));
    }

    // Sort logic
    if (sortBy) {
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'date-desc':
            return new Date(b.Payment_Date) - new Date(a.Payment_Date);
          case 'date-asc':
            return new Date(a.Payment_Date) - new Date(b.Payment_Date);
          case 'amount-desc':
            return b.Amount - a.Amount;
          case 'amount-asc':
            return a.Amount - b.Amount;
          case 'name-asc':
            return a.Mem_Name.localeCompare(b.Mem_Name);
          case 'name-desc':
            return b.Mem_Name.localeCompare(a.Mem_Name);
          default:
            return 0;
        }
      });
    }

    setFilteredTransactions(filtered);
  };

  const resetFilters = () => {
    setFilter({
      startDate: '',
      endDate: '',
      minAmount: '',
      maxAmount: '',
    });
    setSelectedMember(null);
    setSortBy('');
    // Apply filters after reset
    setTimeout(() => applyFilters(), 0);
  };

  const getTotalAmount = () => {
    return filteredTransactions.reduce((sum, t) => sum + Number(t.Amount), 0).toFixed(2);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedMember) count++;
    if (filter.startDate) count++;
    if (filter.endDate) count++;
    if (filter.minAmount) count++;
    if (filter.maxAmount) count++;
    return count;
  };

  const getSortLabel = () => {
    switch (sortBy) {
      case 'date-desc': return 'Date (Newest First)';
      case 'date-asc': return 'Date (Oldest First)';
      case 'amount-desc': return 'Amount (High to Low)';
      case 'amount-asc': return 'Amount (Low to High)';
      case 'name-asc': return 'Member Name (A-Z)';
      case 'name-desc': return 'Member Name (Z-A)';
      default: return 'Sort By';
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif', backgroundColor: '#f5f8fa', color: '#333' }}>
      {/* Header */}
      <header className="dashboard-header">
        <h1>{userData?.company?.Company_Name || 'Company'} Dashboard</h1>
        <button onClick={onLogout} className="logout-button">
          Logout
        </button>
      </header>

      {/* Main Content */}
      <div style={{ flex: 1, padding: '1.5rem' }}>
        {/* Filter and Sort Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          {/* Filter Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#333',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"></polygon>
              </svg>
              Filter
              {getActiveFiltersCount() > 0 && (
                <span style={{ backgroundColor: '#3498db', color: 'white', fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '12px', minWidth: '1.2rem', textAlign: 'center' }}>
                  {getActiveFiltersCount()}
                </span>
              )}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: isFilterOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>

            {isFilterOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                zIndex: 1000,
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1rem',
                minWidth: '300px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                marginTop: '0.5rem'
              }}>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#555', marginBottom: '0.5rem' }}>
                      Member
                    </label>
                    <select
                      value={selectedMember || 'all'}
                      onChange={(e) => handleMemberFilter(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.9rem' }}
                    >
                      <option value="all">All Members</option>
                      {members.map((member) => (
                        <option key={member.Mem_Id} value={member.Mem_Id}>
                          {member.Mem_Name} ({member.Mem_MemberId})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#555', marginBottom: '0.5rem' }}>
                        Start Date
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        value={filter.startDate}
                        onChange={handleFilterChange}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.9rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#555', marginBottom: '0.5rem' }}>
                        End Date
                      </label>
                      <input
                        type="date"
                        name="endDate"
                        value={filter.endDate}
                        onChange={handleFilterChange}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.9rem' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#555', marginBottom: '0.5rem' }}>
                        Min Amount (₹)
                      </label>
                      <input
                        type="number"
                        name="minAmount"
                        value={filter.minAmount}
                        onChange={handleFilterChange}
                        placeholder="0"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.9rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '500', color: '#555', marginBottom: '0.5rem' }}>
                        Max Amount (₹)
                      </label>
                      <input
                        type="number"
                        name="maxAmount"
                        value={filter.maxAmount}
                        onChange={handleFilterChange}
                        placeholder="∞"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.9rem' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #eee' }}>
                    <button
                      onClick={() => {
                        resetFilters();
                        setIsFilterOpen(false);
                      }}
                      style={{ flex: 1, padding: '0.6rem', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                    >
                      Clear All
                    </button>
                    <button
                      onClick={() => {
                        applyFilters();
                        setIsFilterOpen(false);
                      }}
                      style={{ flex: 1, padding: '0.6rem', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sort Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setIsSortOpen(!isSortOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#333',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M7 12h10m-7 6h4"></path>
              </svg>
              {getSortLabel()}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: isSortOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </button>

            {isSortOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                zIndex: 1000,
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                minWidth: '200px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                marginTop: '0.5rem'
              }}>
                {[
                  { value: '', label: 'Default' },
                  { value: 'date-desc', label: 'Date (Newest First)' },
                  { value: 'date-asc', label: 'Date (Oldest First)' },
                  { value: 'amount-desc', label: 'Amount (High to Low)' },
                  { value: 'amount-asc', label: 'Amount (Low to High)' },
                  { value: 'name-asc', label: 'Member Name (A-Z)' },
                  { value: 'name-desc', label: 'Member Name (Z-A)' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                      // Apply sorting immediately with the new sort value
                      setTimeout(() => {
                        let filtered = [...transactions];

                        // Apply filters first
                        if (selectedMember) {
                          filtered = filtered.filter((t) => t.Mem_Id === selectedMember);
                        }

                        if (filter.startDate) {
                          const startDate = new Date(filter.startDate);
                          filtered = filtered.filter((t) => new Date(t.Payment_Date) >= startDate);
                        }

                        if (filter.endDate) {
                          const endDate = new Date(filter.endDate);
                          endDate.setHours(23, 59, 59);
                          filtered = filtered.filter((t) => new Date(t.Payment_Date) <= endDate);
                        }

                        if (filter.minAmount) {
                          filtered = filtered.filter((t) => Number(t.Amount) >= Number(filter.minAmount));
                        }

                        if (filter.maxAmount) {
                          filtered = filtered.filter((t) => Number(t.Amount) <= Number(filter.maxAmount));
                        }

                        // Apply sorting with the new sort value
                        if (option.value) {
                          filtered.sort((a, b) => {
                            switch (option.value) {
                              case 'date-desc':
                                return new Date(b.Payment_Date) - new Date(a.Payment_Date);
                              case 'date-asc':
                                return new Date(a.Payment_Date) - new Date(b.Payment_Date);
                              case 'amount-desc':
                                return Number(b.Amount) - Number(a.Amount);
                              case 'amount-asc':
                                return Number(a.Amount) - Number(b.Amount);
                              case 'name-asc':
                                return a.Mem_Name.localeCompare(b.Mem_Name);
                              case 'name-desc':
                                return b.Mem_Name.localeCompare(a.Mem_Name);
                              default:
                                return 0;
                            }
                          });
                        }

                        setFilteredTransactions(filtered);
                      }, 0);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: 'none',
                      backgroundColor: sortBy === option.value ? '#f8f9fa' : 'transparent',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      color: sortBy === option.value ? '#3498db' : '#333',
                      fontWeight: sortBy === option.value ? '500' : '400'
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Transaction Summary */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem',
          fontSize: '0.95rem',
          fontWeight: '500',
          padding: '0.8rem 1rem',
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 3px rgba(0, 0, 0, 0.05)',
          marginBottom: '1rem',
          flexWrap: 'wrap'
        }}>
          <span>Total Transactions: {filteredTransactions.length}</span>
          <span>Total Amount: ₹{getTotalAmount()}</span>
        </div>

        {/* Transactions Table */}
        {filteredTransactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 3px rgba(0, 0, 0, 0.05)' }}>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>No transactions found</p>
          </div>
        ) : (
          <div style={{ backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: '600', color: '#555', borderBottom: '1px solid #f0f0f0' }}>Date and time</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: '600', color: '#555', borderBottom: '1px solid #f0f0f0' }}>Member Name</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: '600', color: '#555', borderBottom: '1px solid #f0f0f0' }}>Member Phone</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: '600', color: '#555', borderBottom: '1px solid #f0f0f0' }}>Type</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: '600', color: '#555', borderBottom: '1px solid #f0f0f0' }}>Scheme ID/Code</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: '600', color: '#555', borderBottom: '1px solid #f0f0f0' }}>Amount</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: '600', color: '#555', borderBottom: '1px solid #f0f0f0' }}>Payment ID</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: '600', color: '#555', borderBottom: '1px solid #f0f0f0' }}>Receipt No</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction) => (
                  <tr
                    key={transaction.Transaction_Id}
                    style={{ ':hover': { backgroundColor: '#f4f8fb' } }}
                    onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = '#f4f8fb'}
                    onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0' }}>{formatDate(transaction.Payment_Date)}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0' }}>{transaction.Mem_Name}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0' }}>{transaction.Mem_Phone}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0' }}>{transaction.Mem_Type}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0' }}>
                                {transaction.Mem_Type === 'SCHEME' ? transaction.Mem_MemberId : transaction.Mem_Code}
                              </td>


                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0' }}>₹{transaction.Amount}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0' }}>{transaction.Payment_Id}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #f0f0f0' }}>{transaction.Receipt_No}</td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDashboard;